import React, { useState, useRef, useEffect } from 'react';
import { User, Database, Settings, LogOut, Users, X, ArrowRightLeft, Check, ArrowDownUp, Upload, Download, CheckCircle, XCircle, Pause, Play, ChevronUp, ChevronDown, Search, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { databasesApi } from '../../services/api';
import { isEntraAuth } from '../../auth/authConfig';
import { msalInstance } from '../../auth/authConfig';
import { useActivityTracker } from '../../context/ActivityTrackerContext';
import { isDebugMode } from '../../utils/debug';
import logo from '../../assets/logos/WM-Logo-CB-White-Header.png';

const Header = ({ onNavigateHome, setCurrentPage }) => {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { currentUser, isImpersonating, hasPersistedIdentity, users, isAdmin, isSysAdmin, access_Group, roleInDb, impersonate, stopImpersonating, claimIdentity } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const availableDatabases = dbContext?.availableDatabases || [];
  const { activities, pauseActivity, restartActivity, reorderQueue, clearActivity, clearAllCompleted, clearMode, setClearMode } = useActivityTracker();

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showDbSwitcherModal, setShowDbSwitcherModal] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [impersonateSearch, setImpersonateSearch] = useState('');
  // Local DB list for the switcher modal — fetched independently so it works on admin pages too
  const [switcherDatabases, setSwitcherDatabases] = useState([]);

  const userDropdownRef = useRef(null);
  const impersonateRef = useRef(null);
  const dbSwitcherRef = useRef(null);
  const activityPanelRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
      if (impersonateRef.current && !impersonateRef.current.contains(event.target)) {
        setShowImpersonateModal(false);
      }
      if (dbSwitcherRef.current && !dbSwitcherRef.current.contains(event.target)) {
        setShowDbSwitcherModal(false);
      }
      if (activityPanelRef.current && !activityPanelRef.current.contains(event.target)) {
        setShowActivityPanel(false);
        setActivitySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePreferences = () => {
    setShowUserDropdown(false);
    setCurrentPage('preferences');
  };

  const handleSignOut = () => {
    setShowUserDropdown(false);
    if (isEntraAuth() && msalInstance) {
      msalInstance.logoutRedirect();
    } else {
      console.log('Sign out clicked');
    }
  };

  const handleImpersonate = () => {
    setShowUserDropdown(false);
    setImpersonateSearch('');
    setShowImpersonateModal(true);
  };

  const handleSwitchDatabase = () => {
    setShowUserDropdown(false);
    // Fetch databases fresh for the modal (works on both data and admin pages)
    databasesApi.getAvailable()
      .then(res => setSwitcherDatabases(res.data || []))
      .catch(() => setSwitcherDatabases([]));
    setShowDbSwitcherModal(true);
  };

  const handleSelectUser = (user) => {
    impersonate(user);
    setShowImpersonateModal(false);
  };

  const handleSelectDatabase = (db) => {
    const displayName = db.displayName || db.name;
    const slug = displayName.toLowerCase();
    const dbName = db.databaseName || db.id;
    // Update localStorage
    localStorage.setItem('pmd-selected-database', dbName);
    localStorage.setItem('pmd-selected-db-slug', slug);
    window.dispatchEvent(new Event('pmd-database-changed'));
    // Navigate to the new DB home
    navigate(`/${slug}/`);
    setShowDbSwitcherModal(false);
  };

  // Sort users: current user first, then alphabetically; filtered by search
  const sortedUsers = [...users]
    .sort((a, b) => {
      if (a.id === currentUser?.id) return -1;
      if (b.id === currentUser?.id) return 1;
      return (a.user_Name || '').localeCompare(b.user_Name || '');
    })
    .filter(u => !impersonateSearch || (u.user_Name || '').toLowerCase().includes(impersonateSearch.toLowerCase()));

  return (
    <header className="header">
      <div className="header-left">
        <img
          src={logo}
          alt="WM Logo"
          className="header-logo"
          onClick={onNavigateHome}
        />
        <span className="header-title">DAWN</span>
      </div>
      <div className="header-right">
        <div className="header-user-info">
          {isImpersonating ? (
            <div className="header-impersonation-area">
              <div className="header-impersonation-icons">
                {/* Green check — "This is me" (claim identity, persists) */}
                {(!isEntraAuth()) && (
                  <button
                    className="header-imp-btn header-imp-claim"
                    onClick={() => claimIdentity()}
                    title="This is me — remember for next session"
                  >
                    <Check size={13} />
                  </button>
                )}
                {/* Red X — "Go back" (revert to identity) */}
                <button
                  className="header-imp-btn header-imp-revert"
                  onClick={stopImpersonating}
                  title="Stop impersonating"
                >
                  <X size={13} />
                </button>
              </div>
              <div className="header-impersonation-text">
                <span className="header-user-name" style={{ color: '#fca5a5', fontWeight: 600 }}>
                  {currentUser?.user_Name || 'Unknown'}
                </span>
                <span className="header-user-group" style={{ color: '#fca5a5' }}>
                  {dbSlug ? roleInDb(dbSlug) : access_Group}
                </span>
              </div>
            </div>
          ) : (
            <>
              <span className="header-user-name">{currentUser?.user_Name || 'Loading...'}</span>
              <span className="header-user-group">{dbSlug ? roleInDb(dbSlug) : access_Group}</span>
            </>
          )}
        </div>
        {/* ── Activity Tracker Icon ── */}
        <div className="header-activity-wrapper" ref={activityPanelRef}>
          <button
            className={`header-activity-icon${activities.some(a => a.status === 'active') ? ' active' : ''}`}
            onClick={() => { setShowActivityPanel(p => !p); setActivitySearch(''); }}
            title="Data Transfer"
          >
            <ArrowDownUp size={20} />
            {activities.length > 0 && (
              <span className="header-activity-badge">{activities.length}</span>
            )}
          </button>

          {showActivityPanel && (
            <ActivityPanel
              activities={activities}
              search={activitySearch}
              setSearch={setActivitySearch}
              clearMode={clearMode}
              setClearMode={setClearMode}
              onPause={pauseActivity}
              onRestart={restartActivity}
              onReorder={reorderQueue}
              onDismiss={clearActivity}
              onClearAll={clearAllCompleted}
              onHistoryNav={() => {
                setShowActivityPanel(false);
                const slug = localStorage.getItem('pmd-selected-db-slug') || 'gwa';
                navigate(`/${slug}/activity-history`);
              }}
            />
          )}
        </div>

        <div className="header-user-menu" ref={userDropdownRef}>
          <button
            className="header-user-icon"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            <User size={24} />
          </button>
          {showUserDropdown && (
            <div className="header-user-dropdown">
              {availableDatabases.length > 1 && (
                <>
                  <button className="header-user-option" onClick={handleSwitchDatabase}>
                    <ArrowRightLeft size={16} />
                    <span>Switch Database</span>
                  </button>
                  <div className="header-dropdown-divider" />
                </>
              )}
              {(!isEntraAuth() || isSysAdmin) && (
                <>
                  <button className="header-user-option" onClick={handleImpersonate}>
                    <Users size={16} />
                    <span>Impersonate User</span>
                  </button>
                  <div className="header-dropdown-divider" />
                </>
              )}
              <button className="header-user-option" onClick={handlePreferences}>
                <Settings size={16} />
                <span>Preferences</span>
              </button>
              <div className="header-dropdown-divider" />
              <button className="header-user-option" onClick={handleSignOut}>
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <div className="impersonate-overlay">
          <div className="impersonate-modal" ref={impersonateRef}>
            <div className="impersonate-header">
              <h3>Impersonate User</h3>
              <button className="impersonate-close" onClick={() => setShowImpersonateModal(false)}>&times;</button>
            </div>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--color-gray-200)' }}>
              <input
                className="form-input"
                placeholder="Search users..."
                value={impersonateSearch}
                onChange={e => setImpersonateSearch(e.target.value)}
                autoFocus
                style={{ width: '100%' }}
              />
            </div>
            <div className="impersonate-list">
              {sortedUsers.map(user => (
                <button
                  key={user.id}
                  className={`impersonate-user ${user.id === currentUser?.id ? 'current' : ''}`}
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="impersonate-user-info">
                    <span className="impersonate-user-name">{user.user_Name}</span>
                    <span className="impersonate-user-group">{user.organizationName || user.access_Group}</span>
                  </div>
                  {user.id === currentUser?.id && (
                    <span className="impersonate-current-badge">Current</span>
                  )}
                </button>
              ))}
              {sortedUsers.length === 0 && (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)' }}>No users match</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Database Switcher Modal */}
      {showDbSwitcherModal && (
        <div className="impersonate-overlay">
          <div className="db-switcher-modal" ref={dbSwitcherRef}>
            <div className="db-switcher-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flex: 1, minWidth: 0 }}>
                <img src={logo} alt="Wunderlich-Malec" style={{ height: '32px', flexShrink: 0 }} />
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '0.875rem' }}>
                  <div style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.1 }}>DAWN</div>
                  <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem', letterSpacing: '0.04em', lineHeight: 1.2, marginTop: '2px' }}>
                    Documentation, Assets, Wiring &amp; Networks
                  </div>
                </div>
              </div>
              <button
                className="impersonate-close"
                onClick={() => setShowDbSwitcherModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="db-switcher-list">
              {(isSysAdmin ? switcherDatabases : switcherDatabases.filter(db => {
                const slug = (db.displayName || db.name)?.toLowerCase();
                return slug && currentUser?.roles?.[slug];
              })).map(db => {
                const name = db.displayName || db.name;
                const isActive = name?.toLowerCase() === dbSlug;
                return (
                  <button
                    key={db.id || db.databaseName}
                    className={`db-switcher-row ${isActive ? 'current' : ''}`}
                    onClick={() => handleSelectDatabase(db)}
                  >
                    <div className="db-switcher-logo">
                      {db.iconUrl ? (
                        <img src={db.iconUrl} alt="" />
                      ) : (
                        <div className="db-switcher-logo-placeholder">
                          <Database size={16} />
                        </div>
                      )}
                    </div>
                    <div className="db-switcher-info">
                      <span className="db-switcher-name">{name}</span>
                      {db.description && (
                        <span className="db-switcher-desc">{db.description}</span>
                      )}
                    </div>
                    {isActive && (
                      <span className="db-switcher-badge">Current</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ── ActivityPanel ─────────────────────────────────────────────────────────────
const CLEAR_OPTIONS = [
  { label: '1m',    value: 'auto-1m' },
  { label: '1 day', value: 'auto-1d' },
  { label: '1 wk',  value: 'auto-1w' },
  { label: '1 mo',  value: 'auto-1mo' },
];

const FORMAT_COLORS = {
  pdf:    { bg: '#fee2e2', color: '#dc2626' },
  excel:  { bg: '#dcfce7', color: '#16a34a' },
  csv:    { bg: '#dbeafe', color: '#1d4ed8' },
  import: { bg: '#fef9c3', color: '#854d0e' },
};

const ActivityPanel = ({ activities, search, setSearch, clearMode, setClearMode, onPause, onRestart, onReorder, onDismiss, onClearAll, onHistoryNav }) => {
  const debug = isDebugMode();
  const q = search.toLowerCase();
  const filtered = q
    ? activities.filter(a => a.name.toLowerCase().includes(q) || a.format.toLowerCase().includes(q))
    : activities;

  const local    = filtered.filter(a => a.source === 'local');
  const otherTab = filtered.filter(a => a.source === 'other-tab');
  const remote   = filtered.filter(a => a.source === 'remote');

  // Group remote by machineName
  const remoteByMachine = remote.reduce((acc, a) => {
    const key = a.machineName || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  const hasCompleted = activities.some(a => a.status === 'complete' || a.status === 'failed');

  return (
    <div className="header-activity-panel">
      {/* Panel header */}
      <div className="activity-panel-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ArrowDownUp size={14} style={{ color: 'var(--color-primary)' }} />
          <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--color-gray-700)' }}>Data Transfer</span>
        </div>
        {hasCompleted && (
          <button className="activity-clear-all-btn" onClick={onClearAll} title="Clear all completed">
            Clear Completed
          </button>
        )}
      </div>

      {/* Search */}
      <div className="activity-search-row">
        <Search size={12} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />
        <input
          className="activity-search-input"
          placeholder="Filter transfers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Entry list */}
      <div className="activity-entries-scroll">
        {local.length === 0 && otherTab.length === 0 && Object.keys(remoteByMachine).length === 0 && (
          <div className="activity-empty">No active transfers</div>
        )}

        {local.map(a => (
          <ActivityEntry key={a.id} activity={a} debug={debug} isLocal
            onPause={() => onPause(a.id)}
            onRestart={() => onRestart(a.id)}
            onReorderUp={() => onReorder(a.id, 'up')}
            onReorderDown={() => onReorder(a.id, 'down')}
            onDismiss={clearMode === 'manual' ? () => onDismiss(a.id) : null}
          />
        ))}

        {otherTab.length > 0 && (
          <>
            <div className="activity-source-header">This Machine</div>
            {otherTab.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </>
        )}

        {Object.entries(remoteByMachine).map(([machine, entries]) => (
          <React.Fragment key={machine}>
            <div className="activity-source-header">Remote · {machine}</div>
            {entries.map(a => <ActivityEntry key={a.id} activity={a} debug={debug} />)}
          </React.Fragment>
        ))}
      </div>

      {/* Footer */}
      <div className="activity-panel-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '9px', color: 'var(--color-gray-400)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Clear:</span>
          {CLEAR_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setClearMode(opt.value)}
              className={`activity-clear-pill${clearMode === opt.value ? ' selected' : ''}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button className="activity-history-link" onClick={onHistoryNav}>
          <Clock size={11} /> History
        </button>
      </div>
    </div>
  );
};

const ActivityEntry = ({ activity: a, debug, isLocal, onPause, onRestart, onReorderUp, onReorderDown, onDismiss }) => {
  const fmtColor = FORMAT_COLORS[a.format] || { bg: 'var(--color-gray-100)', color: 'var(--color-gray-600)' };
  const isActive  = a.status === 'active';
  const isPaused  = a.status === 'paused';
  const isQueued  = a.status === 'queued';
  const isDone    = a.status === 'complete';
  const isFailed  = a.status === 'failed';

  return (
    <div className={`activity-entry${isDone ? ' done' : ''}${isFailed ? ' failed' : ''}`}>
      <div className="entry-icon-col">
        {a.type === 'upload'
          ? <Upload size={13} style={{ color: 'var(--color-primary)' }} />
          : <Download size={13} style={{ color: '#16a34a' }} />
        }
      </div>
      <div className="entry-main-col">
        <div className="entry-name-row">
          <span className="entry-name">{a.name}</span>
          <span className="entry-format-badge" style={{ background: fmtColor.bg, color: fmtColor.color }}>
            {a.format.toUpperCase()}
          </span>
          {debug && (
            <span style={{ fontSize: '9px', color: 'var(--color-gray-400)', marginLeft: '4px' }}>
              {a.pct}% · {a.source}
            </span>
          )}
        </div>
        <div className="entry-phase">{isQueued ? `Queued` : a.phase}</div>
        {(isActive || isPaused || isQueued) && (
          <div className="entry-progress-track">
            <div className="entry-progress-fill" style={{ width: `${Math.max(2, a.pct)}%`, background: isPaused ? 'var(--color-gray-300)' : isQueued ? 'var(--color-gray-200)' : 'var(--color-primary)' }} />
          </div>
        )}
      </div>
      <div className="entry-actions-col">
        {isDone && <CheckCircle size={14} style={{ color: '#16a34a' }} />}
        {isFailed && <XCircle size={14} style={{ color: '#dc2626' }} />}
        {isLocal && isActive && onPause && (
          <button className="entry-action-btn" onClick={onPause} title="Pause"><Pause size={11} /></button>
        )}
        {isLocal && isPaused && onRestart && (
          <button className="entry-action-btn" onClick={onRestart} title="Restart"><Play size={11} /></button>
        )}
        {isLocal && isQueued && (
          <>
            {onReorderUp && <button className="entry-action-btn" onClick={onReorderUp} title="Move up"><ChevronUp size={11} /></button>}
            {onReorderDown && <button className="entry-action-btn" onClick={onReorderDown} title="Move down"><ChevronDown size={11} /></button>}
          </>
        )}
        {(isDone || isFailed) && onDismiss && (
          <button className="entry-action-btn" onClick={onDismiss} title="Dismiss"><X size={11} /></button>
        )}
        {(isDone) && a.exportRecipe && !isLocal && (
          <button className="entry-redownload-btn" title="Re-download" onClick={() => {
            try {
              const recipe = typeof a.exportRecipe === 'string' ? JSON.parse(a.exportRecipe) : a.exportRecipe;
              window.location.href = recipe.reportPath + '?autoExport=' + recipe.format;
            } catch {}
          }}>↓</button>
        )}
      </div>
    </div>
  );
};

export default Header;
